/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * RequiredDetailsFields
 * ---------------------
 * Gender, height, weight and city — the details every profile must have —
 * plus the optional mobile number. Shared by EditProfileSheet and the first sign-in
 * screen (ProfileDetailsGate) so both ask the same way; the rules live in
 * profileModel (validateRequiredDetails).
 */

import React from 'react';
import {
  GENDER_OPTIONS, GENDER_SELF_DESCRIBE, GENDER_TEXT_MAX, HEIGHT_MIN, HEIGHT_MAX,
  WEIGHT_MIN, WEIGHT_MAX, CITY_MAX, MOBILE_HELPER, genderPickerKey, genderSelfDescription,
  mobileCodeOf, mobileNumberOf, MOBILE_MAX_DIGITS, fitsDecimal, fitsMobile, fitsCountryCode,
} from './profileModel';

/** The form values these fields edit, from personalDetails(). */
export function requiredDetailsForm(details) {
  return {
    gender: genderPickerKey(details.gender),
    genderText: genderSelfDescription(details.gender),
    height: details.heightCm === null ? '' : String(details.heightCm),
    weight: details.weightKg === null ? '' : String(details.weightKg),
    city: details.city || '',
    mobileCode: mobileCodeOf(details.mobile),
    mobileNumber: mobileNumberOf(details.mobile),
  };
}

export default function RequiredDetailsFields({ form, set, errors, idPrefix = 'ap' }) {
  const id = (name) => `${idPrefix}-${name}`;
  // A keystroke or paste whose result the field cannot hold is simply not applied.
  const setIf = (key, fits) => (e) => { if (fits(e.target.value)) set(key)(e); };
  const errProps = (key) => ({ className: `ap-fld${errors[key] ? ' has-err' : ''}` });

  return (
    <>
      <div {...errProps('gender')}>
        <span className="ap-lb" id={id('gender-lb')}>Gender</span>
        <div className="ap-pills" role="radiogroup" aria-labelledby={id('gender-lb')}>
          {GENDER_OPTIONS.map((g) => (
            <React.Fragment key={g.value}>
              <input type="radio" name={id('gender')} id={id(`g-${g.value}`)} value={g.value}
                checked={form.gender === g.value} onChange={set('gender')} />
              <label htmlFor={id(`g-${g.value}`)}>{g.label}</label>
            </React.Fragment>
          ))}
        </div>
        {errors.gender && <span className="ap-err">{errors.gender}</span>}
      </div>
      {form.gender === GENDER_SELF_DESCRIBE && (
        <div {...errProps('genderText')}>
          <label htmlFor={id('gender-text')}>Describe your gender</label>
          <div className="ap-inp has-u">
            <input id={id('gender-text')} value={form.genderText} onChange={set('genderText')}
              maxLength={GENDER_TEXT_MAX} autoComplete="off" aria-describedby={id('gender-count')} />
            <span className="ap-u" id={id('gender-count')}>{form.genderText.length}/{GENDER_TEXT_MAX}</span>
          </div>
          {errors.genderText && <span className="ap-err">{errors.genderText}</span>}
        </div>
      )}
      <div className="ap-f2">
        <div {...errProps('height')}>
          <label htmlFor={id('height')}>Height</label>
          <div className="ap-inp has-u">
            <input id={id('height')} inputMode="decimal" autoComplete="off" value={form.height}
              onChange={setIf('height', fitsDecimal)} aria-describedby={id('height-range')} />
            <span className="ap-u">CM</span>
          </div>
          {errors.height
            ? <span className="ap-err">{errors.height}</span>
            : <span className="ap-help" id={id('height-range')}>{HEIGHT_MIN}–{HEIGHT_MAX} cm</span>}
        </div>
        <div {...errProps('weight')}>
          <label htmlFor={id('weight')}>Weight</label>
          <div className="ap-inp has-u">
            <input id={id('weight')} inputMode="decimal" autoComplete="off" value={form.weight}
              onChange={setIf('weight', fitsDecimal)} aria-describedby={id('weight-range')} />
            <span className="ap-u">KG</span>
          </div>
          {errors.weight
            ? <span className="ap-err">{errors.weight}</span>
            : <span className="ap-help" id={id('weight-range')}>{WEIGHT_MIN}–{WEIGHT_MAX} kg</span>}
        </div>
      </div>
      <div {...errProps('city')}>
        <label htmlFor={id('city')}>City</label>
        <div className="ap-inp">
          <input id={id('city')} value={form.city} onChange={set('city')} autoComplete="address-level2"
            maxLength={CITY_MAX} />
        </div>
        {errors.city && <span className="ap-err">{errors.city}</span>}
      </div>
      <div className={`ap-fld${errors.mobileCode || errors.mobileNumber ? ' has-err' : ''}`}>
        <label htmlFor={id('mobile')}>Mobile number <span className="ap-opt">(optional)</span></label>
        <div className="ap-mobile">
          <div className="ap-inp ap-mobile-code">
            <input id={id('mobile-code')} aria-label="Country code" value={form.mobileCode}
              onChange={setIf('mobileCode', fitsCountryCode)} inputMode="tel" autoComplete="tel-country-code" maxLength={5} />
          </div>
          <div className="ap-inp">
            <input id={id('mobile')} value={form.mobileNumber} onChange={setIf('mobileNumber', fitsMobile)}
              inputMode="numeric" autoComplete="tel-national" maxLength={MOBILE_MAX_DIGITS}
              aria-describedby={id('mobile-help')} />
          </div>
        </div>
        {(errors.mobileCode || errors.mobileNumber)
          && <span className="ap-err">{errors.mobileCode || errors.mobileNumber}</span>}
        <span className="ap-help" id={id('mobile-help')}>{MOBILE_HELPER}</span>
      </div>
    </>
  );
}
